import placeholders from "@pub/placeholders";
import {
  BaseTeaserData,
  BreadCrumbs,
  NaturalCarousel,
  NaturalCarouselRef,
  ResponsiveImage,
  useCustomClassContext,
} from "@sphtech/dmg-design-system";
import cx from "classnames";
import { ReactNode, useEffect, useRef, useState } from "react";

import styles from "./anchorcarousel.module.css";

const IS_VRT = import.meta.env.VITE_DISABLE_EMBEDS === "true";

type Props = {
  data: BaseTeaserData[];
};

export default function AnchorCarousel({ data }: Props): ReactNode {
  const customClass = useCustomClassContext();

  const [position, setPosition] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef<NaturalCarouselRef>(null);

  const onPositionChange = (newPosition: number) => {
    setPosition(newPosition);
  };

  useEffect(() => {
    // To prevent autoplay during VRT
    if (IS_VRT) return;

    const SLIDE_INTERVAL_TIMING = 5_000;
    let slideInterval: NodeJS.Timeout;

    // Only set interval if mouse is NOT hovering over carousel
    if (!isPaused) {
      slideInterval = setInterval(() => {
        if (!carouselRef.current) return;
        carouselRef.current.goTo(position + 1);
      }, SLIDE_INTERVAL_TIMING);
    }

    return () => {
      clearInterval(slideInterval);
    };
  }, [position, isPaused]);

  return (
    <div
      className={styles.carouselContainer}
      onMouseEnter={() => {
        setIsPaused(true);
      }}
      onMouseLeave={() => {
        setIsPaused(false);
      }}
    >
      <NaturalCarousel
        isLooping
        onPositionChange={onPositionChange}
        ref={carouselRef}
        hideAllArrows
      >
        {data.map((item, index) => {
          const croppedImage =
            item.image?.crops.original || placeholders.landscape;

          return (
            <button
              key={index}
              className={styles.item}
              // Using <button> to avoid <a> tag inside another <a> tag
              onClick={() => (window.location.href = item.path)}
              onMouseDown={(e) => {
                // Check for mouse wheel click to open in new window
                if (e.button === 1) {
                  window.open(item.path, "_blank", "noopener,noreferrer");
                }
              }}
            >
              {/* Empty div needed for mobile ResponsiveImage fill contain */}
              <div>
                <ResponsiveImage
                  {...croppedImage}
                  fill="contain"
                  fillAspectRatio={16 / 9}
                  loading={index === 0 ? "eager" : "lazy"}
                />
              </div>

              <section className={styles.info}>
                <div className={styles.infoText}>
                  {item.tags && (
                    <div className={styles.tagsContainer}>
                      {item.tags.slice(0, 5).map((tag, index) => (
                        <a
                          href={tag.urlPath}
                          key={index}
                          className={cx(customClass?.tags?.primary)}
                        >
                          {tag.name.replaceAll(" ", "")}
                        </a>
                      ))}
                    </div>
                  )}

                  <BreadCrumbs sections={item.sections} />

                  {index === 0 ? (
                    <h1 className={styles.title}>{item.title}</h1>
                  ) : (
                    <h3 className={styles.title}>{item.title}</h3>
                  )}

                  <p className={styles.standfirst}>{item.description}</p>
                </div>
              </section>
            </button>
          );
        })}
      </NaturalCarousel>

      <section className={styles.controller}>
        {data.map((_, index) => {
          return (
            <button
              key={index}
              className={cx(
                styles.controllerButton,
                position === index && styles.active,
              )}
              onClick={() => {
                if (!carouselRef.current) return;
                carouselRef.current.goTo(index);
              }}
            ></button>
          );
        })}
      </section>
    </div>
  );
}
